import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  CloudDownload as CloudDownloadIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import {
  RC5AU,
} from '../services/rangeos-api';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../services/rangeos-environment-api';
import { s3BucketsApi } from '../services/s3-buckets-api';

type MappingType = 'draft' | 'existing';

interface ScenarioMapping {
  auId: string;
  auTitle: string;
  scenarioId: string | null;
  scenarioName: string | null;
  mappingType: MappingType; // 'draft' = import from S3, 'existing' = link to existing scenario
  draftScenarioUuid?: string; // Only for 'draft' type - the scenario UUID in S3
  draftScenarioVersion?: string; // Only for 'draft' type - the version in S3
}

interface RangeOSScenario {
  id: string;
  name: string;
  description?: string;
  dateCreated?: string;
  dateEdited?: string;
  author?: string;
  owner?: string;
  isDraftFromS3?: boolean; // Mark scenarios from S3 draft linkages
  draftData?: any; // Store the draft scenario data for import
}

interface ScenarioSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  aus: RC5AU[];
  environmentId: string; // RangeOS Environment ID to use for fetching scenarios
  environmentName?: string; // Optional environment name to display
  s3BucketId?: string; // S3 Bucket ID to fetch draft scenarios from
  s3EnvironmentId?: string; // S3 Environment ID to fetch draft scenarios from
  onSave: (
    mappings: Array<{
      auId: string;
      scenarioId: string;
      scenarioName: string;
      mappingType: MappingType;
      draftScenarioUuid?: string;
      draftScenarioVersion?: string;
    }>,
    environmentId: string
  ) => void;
}

export const ScenarioSelectionDialog: React.FC<ScenarioSelectionDialogProps> = ({
  open,
  onClose,
  aus,
  environmentId,
  environmentName,
  s3BucketId,
  s3EnvironmentId,
  onSave,
}) => {
  const [scenarios, setScenarios] = useState<RangeOSScenario[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mappings, setMappings] = useState<ScenarioMapping[]>([]);
  const [currentAuIndex, setCurrentAuIndex] = useState(0);
  const [orderBy, setOrderBy] = useState<keyof RangeOSScenario>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [currentMappingType, setCurrentMappingType] = useState<MappingType>('draft');

  const handleSearch = useCallback(async (query?: string) => {
    // Use the provided query parameter directly
    // Don't rely on searchQuery state to avoid dependency issues
    if (!query) {
      setScenarios([]);
      setError('Please enter a search term to find scenarios');
      return;
    }

    const searchTerm = query;

    if (!environmentId || typeof searchTerm !== 'string' || !searchTerm.trim()) {
      setScenarios([]);
      setError('Please enter a search term to find scenarios');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log(`[ScenarioDialog] Searching for: "${searchTerm}" in environment: ${environmentId}`);

      // Fetch existing scenarios from RangeOS
      const rangeosScenarios = await rangeosEnvironmentService.searchScenarios(environmentId, searchTerm);
      console.log(`[ScenarioDialog] RangeOS search returned ${rangeosScenarios.length} results:`, rangeosScenarios);

      // Fetch draft scenarios from S3 if bucket/env IDs are provided
      let draftScenarios: RangeOSScenario[] = [];
      if (s3BucketId && s3EnvironmentId) {
        try {
          console.log(`[ScenarioDialog] Fetching draft scenarios from S3 bucket ${s3BucketId}, environment ${s3EnvironmentId}`);
          const s3Drafts = await s3BucketsApi.listDraftScenarios(s3BucketId, s3EnvironmentId);
          console.log(`[ScenarioDialog] Found ${s3Drafts.length} draft scenarios from S3:`, s3Drafts);

          // Filter drafts to match the search term
          const filteredDrafts = s3Drafts.filter(draft =>
            draft.name.toLowerCase().includes(searchTerm.toLowerCase())
          );

          // Convert to RangeOSScenario format
          draftScenarios = filteredDrafts.map(draft => ({
            id: draft.uuid,
            name: draft.name,
            description: `Draft scenario from S3${draft.activityId ? ` (Activity ${draft.activityId})` : ''}`,
            author: draft.draft?.author,
            dateCreated: draft.draft?.dateCreated,
            dateEdited: draft.draft?.dateEdited,
            isDraftFromS3: true,
            draftData: draft.draft,
          }));

          console.log(`[ScenarioDialog] Converted ${draftScenarios.length} S3 drafts to scenario format`);
        } catch (s3Error: any) {
          console.error('[ScenarioDialog] Failed to fetch S3 draft scenarios:', s3Error);
          // Don't fail the whole search if S3 fetch fails
        }
      }

      // Combine and deduplicate results (prioritize RangeOS scenarios over drafts if same UUID)
      const combinedScenarios = [...rangeosScenarios];
      const rangeosIds = new Set(rangeosScenarios.map(s => s.id));

      draftScenarios.forEach(draft => {
        if (!rangeosIds.has(draft.id)) {
          combinedScenarios.push(draft);
        }
      });

      console.log(`[ScenarioDialog] Combined ${combinedScenarios.length} total results (${rangeosScenarios.length} from RangeOS, ${draftScenarios.length} from S3)`);
      setScenarios(combinedScenarios);

      if (combinedScenarios.length === 0) {
        setError(`No scenarios found matching "${searchTerm}". Try a different search term.`);
      }
    } catch (err: any) {
      console.error('[ScenarioDialog] Search failed:', err);
      setError(err.message || 'Failed to search scenarios');
    } finally {
      setLoading(false);
    }
  }, [environmentId, s3BucketId, s3EnvironmentId]);

  useEffect(() => {
    if (open && environmentId) {
      console.log('[ScenarioDialog] Dialog opened, resetting state');
      // Reset state when dialog opens
      setScenarios([]);
      setLoading(false);
      setError(null);
      setOrder('asc');
      setOrderBy('name');
      initializeMappings();

      // Determine initial mapping type for first AU
      const currentAu = aus[0];
      const hasDraftScenario = currentAu?.rangeosScenarioUUID && currentAu?.rangeosScenarioName;

      if (hasDraftScenario) {
        // Default to existing mode to check if scenario already exists in RangeOS
        setCurrentMappingType('existing');
        // Search by scenario name to find if it's already deployed
        if (currentAu?.rangeosScenarioName) {
          setSearchQuery(currentAu.rangeosScenarioName);
          handleSearch(currentAu.rangeosScenarioName);
        } else {
          setSearchQuery('');
        }
      } else {
        // Default to existing if no draft
        setCurrentMappingType('existing');
        if (currentAu?.rangeosScenarioName) {
          setSearchQuery(currentAu.rangeosScenarioName);
          handleSearch(currentAu.rangeosScenarioName);
        } else {
          setSearchQuery('');
        }
      }
    }
  }, [open, aus, environmentId, handleSearch]);

  // Auto-search after user stops typing for 1 second
  useEffect(() => {
    if (!open || !searchQuery.trim()) {
      return;
    }

    console.log(`[ScenarioDialog] Search query changed to: "${searchQuery}", setting up debounce timer`);
    const timeoutId = setTimeout(() => {
      console.log(`[ScenarioDialog] Debounce timer fired, auto-searching for: "${searchQuery}"`);
      handleSearch(searchQuery);
    }, 1000);

    return () => {
      console.log(`[ScenarioDialog] Clearing debounce timer`);
      clearTimeout(timeoutId);
    };
  }, [searchQuery, open, handleSearch]);

  // Auto-map scenarios when search results come back
  useEffect(() => {
    if (!open || scenarios.length === 0 || currentMappingType !== 'existing') {
      return;
    }

    const currentAu = aus[currentAuIndex];
    if (!currentAu?.rangeosScenarioUUID) {
      return; // No scenario UUID to match against
    }

    // Find a scenario that matches the AU's scenario UUID (from auScenarioLinkages in activity metadata)
    const matchedScenario = scenarios.find(s => s.id === currentAu.rangeosScenarioUUID);

    if (matchedScenario) {
      // Check if this is a RangeOS scenario (not S3 draft)
      const isRangeOSScenario = !matchedScenario.isDraftFromS3;
      const currentMapping = mappings[currentAuIndex];

      // Auto-select if:
      // 1. No existing mapping, OR
      // 2. Current mapping is draft type AND we found it in RangeOS (prefer existing over importing draft)
      const shouldAutoMap = !currentMapping?.scenarioId ||
        (currentMapping?.mappingType === 'draft' && isRangeOSScenario);

      if (shouldAutoMap) {
        console.log(`[ScenarioDialog] Auto-mapping AU "${currentAu.title}" (ID: ${currentAu.id}) to ${isRangeOSScenario ? 'existing' : 'draft'} scenario "${matchedScenario.name}" (UUID: ${matchedScenario.id})`);
        handleSelectScenario(matchedScenario);
      }
    }
  }, [scenarios, open, aus, currentAuIndex, mappings, currentMappingType]);

  const initializeMappings = () => {
    console.log('[ScenarioDialog] Initializing mappings for', aus.length, 'AUs');
    const initialMappings: ScenarioMapping[] = aus.map((au) => {
      // Check if AU has draft scenario linked in S3
      const hasDraftScenario = au.rangeosScenarioUUID && au.rangeosScenarioName;
      console.log(`[ScenarioDialog] AU "${au.title}":`, {
        hasDraftScenario,
        rangeosScenarioUUID: au.rangeosScenarioUUID,
        rangeosScenarioName: au.rangeosScenarioName,
        mappedScenarioId: au.mappedScenarioId,
      });

      if (hasDraftScenario) {
        // Default to 'draft' type - will import from S3
        // Extract version from mappedScenarioId if present (format: "uuid:version")
        const version = au.mappedScenarioId?.includes(':')
          ? au.mappedScenarioId.split(':')[1]
          : 'current';

        const draftMapping = {
          auId: au.id,
          auTitle: au.title,
          scenarioId: au.rangeosScenarioUUID,
          scenarioName: au.rangeosScenarioName,
          mappingType: 'draft' as MappingType,
          draftScenarioUuid: au.rangeosScenarioUUID,
          draftScenarioVersion: version,
        };
        console.log('[ScenarioDialog] Initialized as draft mapping:', draftMapping);
        return draftMapping;
      }

      // Priority: Active AU mappings > rc5.yaml scenarios
      if (au.mappedScenarios && au.mappedScenarios.length > 0) {
        // Use the first mapped scenario as existing scenario
        return {
          auId: au.id,
          auTitle: au.title,
          scenarioId: au.mappedScenarios[0],
          scenarioName: au.mappedScenarios[0], // Will be replaced with actual name when scenarios load
          mappingType: 'existing',
        };
      }

      // No mapping yet
      return {
        auId: au.id,
        auTitle: au.title,
        scenarioId: null,
        scenarioName: null,
        mappingType: 'existing', // Default to existing for unmapped AUs
      };
    });
    console.log('[ScenarioDialog] All initial mappings:', initialMappings);
    console.log('[ScenarioDialog] Completed count:', initialMappings.filter(m => m.scenarioId).length);
    setMappings(initialMappings);
    setCurrentAuIndex(0);
  };

  const handleSelectScenario = (scenario: RangeOSScenario) => {
    const updatedMappings = [...mappings];

    // If scenario is from S3 draft, mark as 'draft' type for import
    // Otherwise, mark as 'existing' for linking
    const mappingType = scenario.isDraftFromS3 ? 'draft' : 'existing';

    updatedMappings[currentAuIndex] = {
      ...updatedMappings[currentAuIndex],
      scenarioId: scenario.id,
      scenarioName: scenario.name,
      mappingType,
      // For S3 draft scenarios, store the UUID for import
      ...(scenario.isDraftFromS3 && {
        draftScenarioUuid: scenario.id,
        draftScenarioVersion: 'current',
      }),
    };
    setMappings(updatedMappings);

    // Move to next AU if available
    if (currentAuIndex < aus.length - 1) {
      const nextAu = aus[currentAuIndex + 1];
      setCurrentAuIndex(currentAuIndex + 1);

      // Always start in 'existing' mode to check for deployed scenarios first
      setCurrentMappingType('existing');

      // Pre-search for the next AU's scenario if it has a name
      if (nextAu?.rangeosScenarioName) {
        setSearchQuery(nextAu.rangeosScenarioName);
        handleSearch(nextAu.rangeosScenarioName);
      } else {
        setSearchQuery('');
        setScenarios([]);
      }
    }
  };

  const handleUseDraft = () => {
    const currentAu = aus[currentAuIndex];
    console.log('[ScenarioDialog] handleUseDraft called for AU:', currentAu);
    console.log('[ScenarioDialog] rangeosScenarioUUID:', currentAu?.rangeosScenarioUUID);
    console.log('[ScenarioDialog] rangeosScenarioName:', currentAu?.rangeosScenarioName);

    if (!currentAu?.rangeosScenarioUUID || !currentAu?.rangeosScenarioName) {
      console.log('[ScenarioDialog] Missing UUID or name, cannot use draft');
      return;
    }

    const version = currentAu.mappedScenarioId?.includes(':')
      ? currentAu.mappedScenarioId.split(':')[1]
      : 'current';

    const updatedMappings = [...mappings];
    const newMapping = {
      ...updatedMappings[currentAuIndex],
      scenarioId: currentAu.rangeosScenarioUUID,
      scenarioName: currentAu.rangeosScenarioName,
      mappingType: 'draft' as MappingType,
      draftScenarioUuid: currentAu.rangeosScenarioUUID,
      draftScenarioVersion: version,
    };
    updatedMappings[currentAuIndex] = newMapping;
    console.log('[ScenarioDialog] Updated mapping:', newMapping);
    console.log('[ScenarioDialog] Current AU index:', currentAuIndex);
    console.log('[ScenarioDialog] Total AUs:', aus.length);
    console.log('[ScenarioDialog] All mappings before setState:', updatedMappings);
    setMappings(updatedMappings);
    console.log('[ScenarioDialog] setMappings called');

    // Move to next AU if available
    console.log('[ScenarioDialog] Checking if should move to next AU:', currentAuIndex, '<', aus.length - 1, '=', currentAuIndex < aus.length - 1);
    if (currentAuIndex < aus.length - 1) {
      const nextAu = aus[currentAuIndex + 1];
      setCurrentAuIndex(currentAuIndex + 1);

      // Always start in 'existing' mode to check for deployed scenarios first
      setCurrentMappingType('existing');

      // Pre-search for the next AU's scenario if it has a name
      if (nextAu?.rangeosScenarioName) {
        setSearchQuery(nextAu.rangeosScenarioName);
        handleSearch(nextAu.rangeosScenarioName);
      } else {
        setSearchQuery('');
        setScenarios([]);
      }
    }
  };

  const handleMappingTypeChange = (type: MappingType) => {
    setCurrentMappingType(type);

    if (type === 'existing') {
      // Pre-search if AU has a scenario name
      const currentAu = aus[currentAuIndex];
      if (currentAu?.rangeosScenarioName) {
        setSearchQuery(currentAu.rangeosScenarioName);
        handleSearch(currentAu.rangeosScenarioName);
      }
    } else {
      setSearchQuery('');
      setScenarios([]);
    }
  };

  const handleSkip = () => {
    if (currentAuIndex < aus.length - 1) {
      const nextAu = aus[currentAuIndex + 1];
      setCurrentAuIndex(currentAuIndex + 1);

      // Always start in 'existing' mode to check for deployed scenarios first
      setCurrentMappingType('existing');

      // Pre-search for the next AU's scenario if it has one
      if (nextAu?.rangeosScenarioName) {
        setSearchQuery(nextAu.rangeosScenarioName);
        handleSearch(nextAu.rangeosScenarioName);
      } else {
        setSearchQuery('');
        setScenarios([]);
      }
    }
  };

  const handlePrevious = () => {
    if (currentAuIndex > 0) {
      const prevAu = aus[currentAuIndex - 1];
      setCurrentAuIndex(currentAuIndex - 1);

      // Always start in 'existing' mode to check for deployed scenarios first
      setCurrentMappingType('existing');

      // Pre-search for the previous AU's scenario if it has one
      if (prevAu?.rangeosScenarioName) {
        setSearchQuery(prevAu.rangeosScenarioName);
        handleSearch(prevAu.rangeosScenarioName);
      } else {
        setSearchQuery('');
        setScenarios([]);
      }
    }
  };

  const handleSave = () => {
    if (!environmentId) {
      setError('No RangeOS environment selected');
      return;
    }

    const completeMappings = mappings
      .filter((m) => m.scenarioId && m.scenarioName)
      .map((m) => ({
        auId: m.auId,
        scenarioId: m.scenarioId!,
        scenarioName: m.scenarioName!,
        mappingType: m.mappingType,
        draftScenarioUuid: m.draftScenarioUuid,
        draftScenarioVersion: m.draftScenarioVersion,
      }));

    onSave(completeMappings, environmentId);
  };

  const handleRequestSort = (property: keyof RangeOSScenario) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedScenarios = React.useMemo(() => {
    return [...scenarios].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];

      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return order === 'asc'
        ? aVal < bVal ? -1 : 1
        : aVal > bVal ? -1 : 1;
    });
  }, [scenarios, order, orderBy]);

  const currentAu = aus[currentAuIndex];
  const currentMapping = mappings[currentAuIndex];
  const completedCount = mappings.filter((m) => m.scenarioId).length;

  // Log render state for debugging
  console.log('[ScenarioDialog Render State]', {
    currentAuIndex,
    currentMappingType,
    currentMapping,
    completedCount,
    totalAus: aus.length,
    allMappings: mappings,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Map CMI5 AUs to RangeOS Scenarios</Typography>
            {environmentName && (
              <Typography variant="caption" color="text.secondary">
                Environment: {environmentName}
              </Typography>
            )}
          </Box>
          <Chip
            label={`${completedCount} / ${aus.length} mapped`}
            color={completedCount === aus.length ? 'success' : 'default'}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!environmentId ? (
          <Alert severity="warning">
            No RangeOS environment selected. Please select an environment first.
          </Alert>
        ) : (
          <>
            {/* Current AU Info */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                AU {currentAuIndex + 1} of {aus.length}
              </Typography>
              <Typography variant="h6">{currentAu?.title}</Typography>
              {currentAu?.description && (
                <Typography variant="body2" color="text.secondary">
                  {currentAu.description}
                </Typography>
              )}
              {currentAu?.rangeosScenarioName && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Suggested: <strong>{currentAu.rangeosScenarioName}</strong>
                </Typography>
              )}
              {currentMapping?.scenarioName && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="body2" color="success.main">
                    Mapped to: {currentMapping.scenarioName}
                    {currentMapping.mappingType === 'draft' && ' (from included draft)'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Mapping Type Selection */}
            <Box sx={{ mb: 3 }}>
              <FormLabel component="legend">Scenario Mapping Type</FormLabel>
              <RadioGroup
                row
                value={currentMappingType}
                onChange={(e) => handleMappingTypeChange(e.target.value as MappingType)}
              >
                <FormControlLabel
                  value="draft"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CloudDownloadIcon fontSize="small" />
                      <span>Use Included Draft Export</span>
                    </Box>
                  }
                  disabled={!currentAu?.rangeosScenarioUUID}
                />
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinkIcon fontSize="small" />
                      <span>Map to Existing Scenario</span>
                    </Box>
                  }
                />
              </RadioGroup>
              {!currentAu?.rangeosScenarioUUID && currentMappingType === 'draft' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No draft scenario export available for this AU. Please select an existing scenario.
                </Alert>
              )}
            </Box>

            {/* Draft Scenario Section */}
            {currentMappingType === 'draft' && currentAu?.rangeosScenarioUUID && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                <Typography variant="subtitle2" color="info.main" gutterBottom>
                  Included Draft Scenario
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {currentAu.rangeosScenarioName}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  UUID: {currentAu.rangeosScenarioUUID}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  This scenario draft will be imported to the target RangeOS environment during restore.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CloudDownloadIcon />}
                  onClick={handleUseDraft}
                  sx={{ mt: 2 }}
                >
                  Use This Draft
                </Button>
              </Box>
            )}

            {/* Search Section - Only show for existing type */}
            {currentMappingType === 'existing' && (
              <>
                <TextField
              fullWidth
              placeholder="Search scenarios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => handleSearch(searchQuery)}>
                      Search
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Scenarios Table */}
            {(() => {
              console.log(`[ScenarioDialog Render] loading=${loading}, scenarios.length=${scenarios.length}, searchQuery="${searchQuery}", searchQuery.trim()="${searchQuery.trim()}"`);
              return null;
            })()}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : scenarios.length === 0 && !searchQuery.trim() ? (
              <Alert severity="info">
                Enter a search term above to find scenarios. Start typing to search by name.
              </Alert>
            ) : scenarios.length === 0 ? (
              <Alert severity="warning">
                No scenarios found. Try a different search term or check if scenarios exist in this environment.
              </Alert>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'name'}
                          direction={orderBy === 'name' ? order : 'asc'}
                          onClick={() => handleRequestSort('name')}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'description'}
                          direction={orderBy === 'description' ? order : 'asc'}
                          onClick={() => handleRequestSort('description')}
                        >
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'author'}
                          direction={orderBy === 'author' ? order : 'asc'}
                          onClick={() => handleRequestSort('author')}
                        >
                          Author
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'owner'}
                          direction={orderBy === 'owner' ? order : 'asc'}
                          onClick={() => handleRequestSort('owner')}
                        >
                          Owner
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'dateCreated'}
                          direction={orderBy === 'dateCreated' ? order : 'asc'}
                          onClick={() => handleRequestSort('dateCreated')}
                        >
                          Created
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'dateEdited'}
                          direction={orderBy === 'dateEdited' ? order : 'asc'}
                          onClick={() => handleRequestSort('dateEdited')}
                        >
                          Updated
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedScenarios.map((scenario) => (
                      <TableRow
                        key={scenario.id}
                        hover
                        onClick={() => handleSelectScenario(scenario)}
                        selected={currentMapping?.scenarioId === scenario.id}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {scenario.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {scenario.id}
                              </Typography>
                            </Box>
                            {scenario.isDraftFromS3 && (
                              <Chip
                                label="Draft from S3"
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 300 }}>
                            {scenario.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {scenario.author || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {scenario.owner || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {scenario.dateCreated
                              ? new Date(scenario.dateCreated).toLocaleDateString()
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {scenario.dateEdited
                              ? new Date(scenario.dateEdited).toLocaleDateString()
                              : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box>
          <Button onClick={handlePrevious} disabled={currentAuIndex === 0}>
            Previous
          </Button>
          <Button onClick={handleSkip} disabled={currentAuIndex === aus.length - 1}>
            Skip
          </Button>
        </Box>
        <Box>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={completedCount === 0}
          >
            Save Mappings ({completedCount})
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
